import { useState, useEffect } from "react";
import { NavLink, Outlet, useLocation, Link } from "react-router-dom";
import YasamaruHelp from "@/components/YasamaruHelp";
import { InquiryDialog } from "@/components/InquiryDialog";
import SubscriptionGate from "@/components/SubscriptionGate";
import {
  Users,
  Building2,
  Briefcase,
  CalendarRange,
  CalendarCheck,
  MessageSquare,
  Mail,
  LifeBuoy,
  LogOut,
  CalendarPlus,
  ClipboardList,
  UserCheck,
  Layers,
  Menu,
  X,
  Inbox,
  UserCog,
  Ticket,
  HelpCircle,
  Store,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

function NavItem({ to, label, icon: Icon, end, testId, onNavigate }) {
  return (
    <NavLink
      to={to}
      end={end}
      data-testid={testId}
      onClick={onNavigate}
      className={({ isActive }) =>
        `flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
          isActive
            ? "bg-blue-50 text-blue-700"
            : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
        }`
      }
    >
      <Icon className="w-5 h-5 shrink-0" />
      <span>{label}</span>
    </NavLink>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const isAdmin = user?.role === "admin";
  const base = isAdmin ? "/admin" : "/manager";
  const [open, setOpen] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const { pathname } = useLocation();
  const impersonatedBy = typeof window !== "undefined" ? sessionStorage.getItem("impersonated_by") : null;

  // Auto-close on route change (mobile)
  useEffect(() => { setOpen(false); }, [pathname]);

  const closeMenu = () => setOpen(false);

  const endImpersonation = () => {
    sessionStorage.removeItem("shift_token");
    sessionStorage.removeItem("shift_user");
    sessionStorage.removeItem("impersonated_by");
    window.close();
    // 閉じられない場合
    setTimeout(() => window.location.href = "/admin/tenants", 300);
  };

  return (
    <SubscriptionGate user={user}>
    <div className="min-h-screen bg-slate-50">
      {/* 代理ログイン中バナー */}
      {impersonatedBy && (
        <div className="sticky top-0 z-40 bg-amber-500 text-white text-xs font-bold px-4 py-1.5 flex items-center justify-between" data-testid="impersonation-banner">
          <span>⚠️ 運営として「{user?.name || ""}」に代理ログイン中（{impersonatedBy}） — すべての操作は店舗の運営アクセス履歴に記録されます</span>
          <button
            onClick={endImpersonation}
            className="ml-2 px-2 py-0.5 bg-white text-amber-700 rounded text-[10px] font-bold hover:bg-amber-50"
            data-testid="end-impersonation"
          >
            代理ログイン終了
          </button>
        </div>
      )}
      {/* Mobile/tablet top bar */}
      <header className="xl:hidden sticky top-0 z-30 bg-white border-b border-slate-200 flex items-center justify-between px-3 h-14">
        <button
          type="button"
          data-testid="hamburger-button"
          onClick={() => setOpen(true)}
          aria-label="メニューを開く"
          className="w-10 h-10 flex items-center justify-center rounded-md hover:bg-slate-100"
        >
          <Menu className="w-6 h-6 text-slate-700" />
        </button>
        <div className="flex items-center gap-2">
          <img src="/yasamaru-64.png" alt="やさまるシフト" className="w-8 h-8" />
          <span className="text-sm font-bold text-slate-900">やさまるシフト</span>
        </div>
        <div className="w-10" />
      </header>

      {/* Backdrop for mobile */}
      {open && (
        <div
          className="xl:hidden fixed inset-0 bg-slate-900/40 z-40 backdrop-blur-sm"
          onClick={closeMenu}
          data-testid="sidebar-backdrop"
        />
      )}

      {/* Sidebar */}
      <aside
        data-testid="admin-sidebar"
        className={`
          w-64 bg-white border-r border-slate-200 flex flex-col z-50
          fixed inset-y-0 left-0
          transition-transform duration-200 ease-out
          ${open ? "translate-x-0 shadow-2xl" : "-translate-x-full"}
          xl:translate-x-0 xl:shadow-none
        `}
      >
        <div className="px-5 py-5 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/yasamaru-64.png" alt="やさまるシフト" className="w-11 h-11 shrink-0" />
            <div>
              <h1 className="text-base font-bold text-slate-900 leading-tight">やさまるシフト</h1>
              <p className="text-xs text-slate-500 leading-tight">
                {isAdmin ? "管理者" : "担当者"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={closeMenu}
            className="xl:hidden w-8 h-8 flex items-center justify-center rounded-md hover:bg-slate-100"
            aria-label="メニューを閉じる"
            data-testid="sidebar-close"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {isAdmin ? (
            <>
              <div className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                セールス
              </div>
              <NavItem to="/admin/leads" label="トライアル申込み" icon={Mail} testId="nav-leads" onNavigate={closeMenu} />
              <NavItem to="/admin/inquiries" label="お問い合わせ" icon={Inbox} testId="nav-inquiries" onNavigate={closeMenu} />
              <NavItem to="/admin/coupons" label="クーポン管理" icon={Ticket} testId="nav-coupons" onNavigate={closeMenu} />
              <NavItem to="/admin/tenants" label="テナント / 代理ログイン" icon={Store} testId="nav-tenants" onNavigate={closeMenu} />

              <div className="px-2 pt-4 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                設定
              </div>
              <NavItem to="/admin/profile" label="アカウント設定" icon={UserCog} testId="nav-profile" onNavigate={closeMenu} />
              <NavItem to="/admin/help" label="使い方ガイド" icon={HelpCircle} testId="nav-help-admin" onNavigate={closeMenu} />
            </>
          ) : (
            <>
              <div className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                シフト
              </div>
              <NavItem to={`${base}/shift-creation`} label="シフト作成" icon={CalendarPlus} testId="nav-shift-creation" onNavigate={closeMenu} />
              <NavItem to={`${base}/staff-status`} label="スタッフ状況一覧" icon={UserCheck} testId="nav-staff-status" onNavigate={closeMenu} />
              <NavItem to={`${base}/confirmed`} label="確定シフト" icon={CalendarCheck} testId="nav-confirmed" onNavigate={closeMenu} />
              <NavItem to={`${base}/messages`} label="メッセージ" icon={MessageSquare} testId="nav-messages" onNavigate={closeMenu} />

              <div className="px-2 pt-4 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                マスタ
              </div>
              <NavItem to="/manager/staff" label="スタッフ管理" icon={Users} testId="nav-staff-mgmt-manager" onNavigate={closeMenu} />
              <NavItem to="/manager/assignments" label="配置マスタ" icon={Layers} testId="nav-assignments-manager" onNavigate={closeMenu} />

              <div className="px-2 pt-4 pb-1 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
                サポート
              </div>
              <NavItem to="/manager/profile" label="アカウント設定" icon={UserCog} testId="nav-profile-manager" onNavigate={closeMenu} />
              <NavItem to="/manager/help" label="使い方ガイド" icon={HelpCircle} testId="nav-help-manager" onNavigate={closeMenu} />
              <button
                type="button"
                data-testid="open-inquiry-manager"
                onClick={() => { setInquiryOpen(true); closeMenu(); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
              >
                <LifeBuoy className="w-5 h-5 shrink-0" />
                <span>お問い合わせ</span>
              </button>
            </>
          )}
        </nav>

        <div className="border-t border-slate-200 p-3">
          <div className="px-2 pb-2">
            <p className="text-sm font-medium text-slate-900">{user?.name}</p>
            <p className="text-xs text-slate-500">@{user?.login_id}</p>
          </div>
          <button
            data-testid="admin-logout-button"
            onClick={logout}
            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-md"
          >
            <LogOut className="w-4 h-4" />
            ログアウト
          </button>
        </div>
      </aside>

      <main className="xl:ml-64 p-4 md:p-6 xl:p-8">
        <Outlet />
      </main>
      <YasamaruHelp />
      <InquiryDialog open={inquiryOpen} onOpenChange={setInquiryOpen} />
    </div>
    </SubscriptionGate>
  );
}
